import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrand } from './brand';
import { useViewer } from './shell';
import { useTrack } from './track';
import { PREF, readPref, writePref } from './lib/prefs';
import './uiVersion.css';

export type UiVersion = 'classic' | 'new';
const VERSIONS: readonly UiVersion[] = ['classic', 'new'];

/**
 * Which design this viewer sees. Signed-out visitors always get the new look
 * (the preview, public tips pages and the landing page). Signed-in members start
 * on the new look too, and can go back to classic from the menu switch (or a
 * ?ui=classic link); a member who chose classic stays there until they switch. `ready` waits for the sign-in check only,
 * so nobody flashes the wrong design.
 */
export function useUiVersion() {
  const viewer = useViewer();
  const { search } = useLocation();
  const track = useTrack();
  const brand = useBrand();
  const canChoose = viewer.isAuthenticated && brand.features.classic !== false;
  const fromLink = new URLSearchParams(search).get('ui');
  const linked = (VERSIONS as readonly string[]).includes(fromLink ?? '') ? (fromLink as UiVersion) : null;
  const wanted = linked ?? readPref<UiVersion>(PREF.uiVersion, 'new', VERSIONS);
  const version: UiVersion = canChoose ? wanted : 'new';
  const ready = !(viewer.authLoading ?? viewer.loading);

  useEffect(() => {
    if (canChoose && linked) writePref(PREF.uiVersion, linked);
  }, [canChoose, linked]);

  const switchTo = (to: UiVersion, source = 'menu') => {
    track('ui_version_switch', { from: version, to, source, uiVersion: version });
    writePref(PREF.uiVersion, to);
    // A full load: the classic pages' global CSS must never share a page with the new look
    const url = new URL(window.location.href);
    url.searchParams.delete('ui');
    window.location.assign(url.toString());
  };

  return { canChoose, version, ready, switchTo };
}

/**
 * Renders the new look (children) or the classic element, per useUiVersion. It waits for
 * the first sign-in check only: registering or logging in flips auth back to loading, and
 * hiding the page then unmounted it, so a failed sign-up came back as a blank form with
 * its error message gone.
 */
export function Versioned({ classic, children }: { classic: ReactNode; children: ReactNode }) {
  const { version, ready } = useUiVersion();
  const settled = useRef(false);
  if (ready) settled.current = true;
  if (!settled.current) return null;
  return version === 'new' ? <>{children}</> : <Suspense fallback={null}>{classic}</Suspense>;
}

/** "Try the new look" / "Back to classic", for signed-in members. */
export function UiVersionSwitch({ className }: { className?: string }) {
  const { canChoose, version, switchTo } = useUiVersion();
  if (!canChoose) return null;
  return (
    <button type="button" className={className} onClick={() => switchTo(version === 'new' ? 'classic' : 'new')}>
      {version === 'new' ? 'Back to classic' : 'Try the new look'}
    </button>
  );
}

/** On the classic pages (members who chose classic): the invitation back to the new look, or dismiss it for good. */
export function NewLookBanner() {
  const { canChoose, version, switchTo } = useUiVersion();
  const track = useTrack();
  const [dismissed, setDismissed] = useState(() => readPref(PREF.newLookBanner, 'shown', ['shown', 'dismissed'] as const) === 'dismissed');
  if (!canChoose || version === 'new' || dismissed) return null;
  return (
    <div className="ui-newlook" role="region" aria-label="New look">
      <span className="ui-newlook-tx">
        <b>Our new look is here.</b> A cleaner layout, speed maps and results inside each race. You can switch back any time.
      </span>
      <button type="button" className="ui-newlook-go" onClick={() => switchTo('new', 'banner')}>
        Try the new look
      </button>
      <button
        type="button"
        className="ui-newlook-x"
        aria-label="Dismiss"
        onClick={() => {
          writePref(PREF.newLookBanner, 'dismissed');
          setDismissed(true);
          track('ui_click', { target: 'new_look_banner_dismiss', uiVersion: 'classic' });
        }}
      >
        ×
      </button>
    </div>
  );
}
